import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  pageTitleClass,
  pageSubtitleClass,
  tabButtonClass,
} from "../dashboardStyles";
import { SMS_CENTER_PATH } from "../../../constants/sms";
import SubscriptionPanel from "../SubscriptionPanel";
import SmsCenter from "./SmsCenter";
import PaymentHistoryPanel from "../PaymentHistoryPanel";

/**
 * The billing page's tabs, each at an address of its own.
 *
 * Addresses rather than local state because each one is linked to from
 * somewhere that cannot pass state: Settings → Notifications and the low-credit
 * email point at the SMS tab, and NETOPIA sends a customer back from the card
 * form to PaymentReturn, which links to the tab the payment was for. All three
 * sit under `/dashboard/billing`, so canAccessPath's prefix match gives them the
 * billing item's permissions without any of them becoming a sidebar entry.
 */
export const BILLING_TABS = [
  {
    id: "plan",
    path: "/dashboard/billing",
    labelKey: "Dashboard.Billing.tabs.plan",
    Panel: SubscriptionPanel,
  },
  {
    id: "sms",
    path: SMS_CENTER_PATH,
    labelKey: "Dashboard.Billing.tabs.sms",
    Panel: SmsCenter,
  },
  {
    id: "payments",
    path: "/dashboard/billing/payments",
    labelKey: "Dashboard.Billing.tabs.payments",
    Panel: PaymentHistoryPanel,
  },
];

/**
 * The billing page: what the business pays for, the SMS credit it buys, and
 * the payments and invoices that came of both — one tab each, only the active
 * one rendered.
 */
const Billing = ({ tab = "plan" }) => {
  const { t } = useTranslation();

  const active = BILLING_TABS.find((item) => item.id === tab) ?? BILLING_TABS[0];
  const { Panel } = active;

  return (
    <div>
      <div className="mb-5">
        <h1 className={pageTitleClass}>{t("Dashboard.Billing.title")}</h1>
        <p className={pageSubtitleClass}>{t("Dashboard.Billing.subtitle")}</p>
      </div>

      {/* Links, not buttons: each tab is a page, so it is announced with
          aria-current and opens in a new tab like any other link. On a phone
          the strip scrolls inside itself — reaching to the screen edges —
          rather than squeezing the labels or widening the page. */}
      <div className="mb-6 max-sm:-mx-4 max-sm:overflow-x-auto max-sm:px-4 max-sm:py-1">
        <nav
          aria-label={t("Dashboard.Billing.tabs.label")}
          className="flex w-max min-w-full gap-6 border-b border-ink-200"
          data-testid="billing-tabs"
        >
          {BILLING_TABS.map((item) => {
            const isActive = item.id === active.id;
            return (
              <Link
                key={item.id}
                to={item.path}
                aria-current={isActive ? "page" : undefined}
                className={`${tabButtonClass(isActive)} inline-flex min-h-11 items-center no-underline`}
              >
                {t(item.labelKey)}
              </Link>
            );
          })}
        </nav>
      </div>

      <Panel />
    </div>
  );
};

Billing.propTypes = {
  tab: PropTypes.oneOf(BILLING_TABS.map((item) => item.id)),
};

export default Billing;
